import React, { useEffect, useState } from "react";
import {
  Popover,
  Button,
  Box,
  Typography,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IPincodeResponseDTO } from "../../interfaces/Orders";
import { getWarehousePincodes, getOrdersList } from "../../services/Orders";

const STATUS_OPTIONS = [
  "Unpicked",
  "Unpacked",
  "Packed",
  "Assigned",
  "Received",
  "Dispatched",
  "Out_for_delivery",
  "Delivered",
  "Delayed",
];

interface FilterDialogProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: { statuses: string[]; deliveryFrom: string; deliveryTo: string; pincodes: string[]; costFrom: string; costTo: string }, data: any[]) => void;
  companyId: number;
  search: string;
}

const FilterDialog = ({ open, anchorEl, onClose, onApply, companyId, search }: FilterDialogProps) => {
  const [tempSelected, setTempSelected] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState("Status");
  const [deliveryFrom, setDeliveryFrom] = useState("");
  const [deliveryTo, setDeliveryTo] = useState("");
  const [pincodes, setPincodes] = useState<string[]>([]);
  const [costFrom, setCostFrom] = useState("");
  const [costTo, setCostTo] = useState("");
  const [pincodeData, setPincodeData] = useState<IPincodeResponseDTO[]>([]);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setPincodeLoading(true);
        const res = await getWarehousePincodes();
        setPincodeData(Array.isArray(res) ? res : []);
      } catch {
        setPincodeData([]);
      } finally {
        setPincodeLoading(false);
      }
    })();
  }, [open]);

  const PINCODE_OPTIONS = pincodeData.map(p => p.pincode);

  const handleToggle = (status: string) => {
    setTempSelected((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handlePincodeToggle = (pin: string) => {
    setPincodes((prev) =>
      prev.includes(pin) ? prev.filter((p) => p !== pin) : [...prev, pin]
    );
  };

  const handleSelectAll = () => {
    if (activeFilter === "Status") {
      setTempSelected(tempSelected.length === STATUS_OPTIONS.length ? [] : [...STATUS_OPTIONS]);
    } else if (activeFilter === "Pincode") {
      setPincodes(pincodes.length === PINCODE_OPTIONS.length ? [] : [...PINCODE_OPTIONS]);
    }
  };

  const handleClearAll = () => {
    setTempSelected([]);
    setDeliveryFrom("");
    setDeliveryTo("");
    setPincodes([]);
    setCostFrom("");
    setCostTo("");
  };

  const handleApply = async () => {
    try {
      setApplying(true);
      const orderId = search ? Number(search) || undefined : undefined;
      const res = await getOrdersList(
        companyId,
        orderId,
        tempSelected.length ? tempSelected : undefined,
        deliveryFrom ? `${deliveryFrom}T00:00:00` : undefined,
        deliveryTo ? `${deliveryTo}T23:59:59` : undefined,
        pincodes.length ? pincodes : undefined,
        costFrom || undefined,
        costTo || undefined
      );
      onApply({ statuses: tempSelected, deliveryFrom, deliveryTo, pincodes, costFrom, costTo }, Array.isArray(res) ? res : []);
    } catch {
      onApply({ statuses: tempSelected, deliveryFrom, deliveryTo, pincodes, costFrom, costTo }, []);
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const renderRightContent = () => {
    switch (activeFilter) {
      case "Status":
        return (
          <Box sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: "6px", p: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={tempSelected.length === STATUS_OPTIONS.length}
                  indeterminate={tempSelected.length > 0 && tempSelected.length < STATUS_OPTIONS.length}
                  onChange={handleSelectAll}
                  sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
                />
              }
              label={<Typography variant="body2">Select All</Typography>}
            />
            <Divider />
            <Box sx={{ maxHeight: "180px", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
              <FormGroup>
                {STATUS_OPTIONS.map((status) => (
                  <FormControlLabel
                    key={status}
                    control={
                      <Checkbox
                        checked={tempSelected.includes(status)}
                        onChange={() => handleToggle(status)}
                        sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" } }}
                      />
                    }
                    label={<Typography variant="body2">{status.replace(/_/g, " ")}</Typography>}
                  />
                ))}
              </FormGroup>
            </Box>
          </Box>
        );
      case "Delivery Date":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>From</Typography>
              <TextField fullWidth size="small" type="date" value={deliveryFrom} onChange={(e) => setDeliveryFrom(e.target.value)} sx={{ backgroundColor: "#F9FAFB", "& .MuiOutlinedInput-root": { borderRadius: "6px", "& fieldset": { borderColor: "rgba(74,85,101,0.15)" }, "&:hover fieldset": { borderColor: "#000" }, "&.Mui-focused fieldset": { borderColor: "#000" } }, "& .MuiInputBase-input": { fontSize: "12px", padding: "12px" } }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>To</Typography>
              <TextField fullWidth size="small" type="date" value={deliveryTo} onChange={(e) => setDeliveryTo(e.target.value)} sx={{ backgroundColor: "#F9FAFB", "& .MuiOutlinedInput-root": { borderRadius: "6px", "& fieldset": { borderColor: "rgba(74,85,101,0.15)" }, "&:hover fieldset": { borderColor: "#000" }, "&.Mui-focused fieldset": { borderColor: "#000" } }, "& .MuiInputBase-input": { fontSize: "12px", padding: "12px" } }} />
            </Box>
          </Box>
        );
      case "Pincode":
        if (pincodeLoading) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          );
        }
        if (PINCODE_OPTIONS.length === 0) {
          return <Typography variant="body2" color="grey.500" sx={{ py: 2, textAlign: "center" }}>No pincodes available</Typography>;
        }
        return (
          <Box sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: "6px", p: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={pincodes.length === PINCODE_OPTIONS.length}
                  indeterminate={pincodes.length > 0 && pincodes.length < PINCODE_OPTIONS.length}
                  onChange={handleSelectAll}
                  sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
                />
              }
              label={<Typography variant="body2">Select All</Typography>}
            />
            <Divider />
            <Box sx={{ maxHeight: "180px", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
              <FormGroup>
                {PINCODE_OPTIONS.map((pin) => (
                  <FormControlLabel
                    key={pin}
                    control={
                      <Checkbox
                        checked={pincodes.includes(pin)}
                        onChange={() => handlePincodeToggle(pin)}
                        sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" } }}
                      />
                    }
                    label={<Typography variant="body2">{pin}</Typography>}
                  />
                ))}
              </FormGroup>
            </Box>
          </Box>
        );
      case "Cost":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>From</Typography>
              <TextField fullWidth size="small" type="number" placeholder="1000" value={costFrom} onChange={(e) => setCostFrom(e.target.value)} InputProps={{ endAdornment: (<InputAdornment position="end"><Typography variant="body2" sx={{ color: "grey.500" }}>₹</Typography></InputAdornment>) }} sx={{ backgroundColor: "#F9FAFB", "& .MuiOutlinedInput-root": { borderRadius: "6px", "& fieldset": { borderColor: "rgba(74,85,101,0.15)" }, "&:hover fieldset": { borderColor: "#000" }, "&.Mui-focused fieldset": { borderColor: "#000" } }, "& .MuiInputBase-input": { fontSize: "12px", padding: "12px" }, "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 }, "& input[type=number]": { MozAppearance: "textfield" } }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>To</Typography>
              <TextField fullWidth size="small" type="number" placeholder="50000" value={costTo} onChange={(e) => setCostTo(e.target.value)} InputProps={{ endAdornment: (<InputAdornment position="end"><Typography variant="body2" sx={{ color: "grey.500" }}>₹</Typography></InputAdornment>) }} sx={{ backgroundColor: "#F9FAFB", "& .MuiOutlinedInput-root": { borderRadius: "6px", "& fieldset": { borderColor: "rgba(74,85,101,0.15)" }, "&:hover fieldset": { borderColor: "#000" }, "&.Mui-focused fieldset": { borderColor: "#000" } }, "& .MuiInputBase-input": { fontSize: "12px", padding: "12px" }, "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 }, "& input[type=number]": { MozAppearance: "textfield" } }} />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { borderRadius: "0.75rem", width: "450px", mt: 1 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>Filter</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" sx={{ color: "#000" }} />
        </IconButton>
      </Box>
      
      <Divider />

      <Box sx={{ p: 0, display: "flex", height: "280px", backgroundColor: "background.default" }}>
        <Box sx={{ width: "40%", borderRight: "1px solid", borderColor: "grey.300", p: 1.5, backgroundColor: "background.default" }}>
          {["Status", "Delivery Date", "Pincode", "Cost"].map((filter) => (
            <Box
              key={filter}
              onClick={() => setActiveFilter(filter)}
              sx={{
                p: 1,
                cursor: "pointer",
                borderRadius: "6px",
                mb: 0.5,
                backgroundColor: activeFilter === filter ? "white" : "transparent",
                color: activeFilter === filter ? "primary.main" : "text.primary",
                fontWeight: activeFilter === filter ? 600 : 400,
              }}
            >
              <Typography variant="body2">{filter}</Typography>
            </Box>
          ))}
        </Box>
        
        <Box sx={{ width: "60%", p: 1.5, backgroundColor: "white", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
          {renderRightContent()}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", backgroundColor: "background.default" }}>
        <Button onClick={handleClearAll} sx={{ textTransform: "none", color: "text.primary", "&:hover": { backgroundColor: "transparent" } }}>
          Clear all
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose} sx={{ borderRadius: "6px", textTransform: "none", borderColor: "grey.300", color: "text.primary", backgroundColor: "#fff", "&:hover": { backgroundColor: "#fff", borderColor: "grey.300" } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={applying} sx={{ borderRadius: "6px", textTransform: "none", backgroundColor: "primary.main", "&:hover": { backgroundColor: "primary.main" } }}>
            {applying ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "OK"}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
export default FilterDialog;
